import { useEffect, useState } from "react";
import { Form, Input, Button } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../../../shared/api/axiosInstance";
import FormAlert from "../../../shared/components/form-alert/FormAlert";
import { deleteCookie } from "../../../shared/utils/helpers";
import { useAuth } from "../hooks/useAuth";
import { trackException, trackInfo, LogLevel } from "../../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../../shared/services/appInsightsConstants";

type MessageType = "success" | "danger" | "warning" | "info";

interface ForgotPasswordFormData {
  emailAddress: string;
}

const ForgotPassword = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType | null>(null);
  const [loading, setLoading] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Handle redirect after success message is shown
  useEffect(() => {
    if (shouldRedirect) {      
      const timer = setTimeout(() => {
        navigate("/account/login");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [shouldRedirect, navigate]);

  const handleSubmit = async ({ emailAddress }: ForgotPasswordFormData) => {
    setLoading(true);
    setMessage(null);
    setMessageType(null);

    // Track password reset attempt
    trackInfo("Password reset request initiated", { 
      emailAddress,
      component: AppInsightsComponents.FORGOT_PASSWORD,
      action: AppInsightsActions.PASSWORD_RESET_ATTEMPT
    });

    try {
      const response = await axiosInstance.post(
        "/account/ResetForgottenPassword",
        { emailAddress }
      );

      if (response.status !== 200) {
        throw new Error(
          "Failed to receive new password request. Please try again later or contact MSI."
        );
      }
      
      if (isAuthenticated) {
        await axiosInstance.post(
          "/token/logout",
          {},
          { withCredentials: true }
        );
        // Always delete the cookie regardless of API success
        deleteCookie("MSILoggedInEmailAddress");
      }

      // Track successful password reset request
      trackInfo("Password reset request successful", { 
        emailAddress,
        component: AppInsightsComponents.FORGOT_PASSWORD,
        action: AppInsightsActions.PASSWORD_RESET_SUCCESS
      });

      setMessage(
        "Your request has been received and new password will be emailed to you. You will be redirected to login page."
      );
      setMessageType("success");
      setShouldRedirect(true);
    } catch (error) {
      // Track password reset failure
      trackException(
        error as Error,
        LogLevel.Error,
        { 
          emailAddress,
          component: AppInsightsComponents.FORGOT_PASSWORD,
          action: AppInsightsActions.PASSWORD_RESET_FAILURE,
          errorType: axios.isAxiosError(error) ? AppInsightsErrorTypes.API_ERROR : AppInsightsErrorTypes.UNEXPECTED_ERROR,
          statusCode: axios.isAxiosError(error) ? (error.response?.status?.toString() || "unknown") : "unknown"
        }
      );

      if (axios.isAxiosError(error) && error.response?.data) {
        const responseData = error.response.data;
        const errorMessage =
          typeof responseData === "string"
            ? responseData
            : responseData?.message ||
              "An unexpected error occurred. Please try again later or contact MSI.";
        setMessage(errorMessage);
        setMessageType("danger");
      } else {
        setMessage(
          "An unexpected error occurred. Please try again later or contact MSI."
        );
        setMessageType("danger");
      }

      setLoading(false);
    }
  };

  const handleInputChange = () => {
    setMessage(null);
    setMessageType(null);
  };

  return (
    <div className="form col-span-6 md:col-span-3 lg:col-span-4">
      <div className="grid grid-cols-8">
        <div className="col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1 2xl:col-span-1">
          <h6 className="whitespace-nowrap">Forgot Password</h6>
        </div>
        <div className="m-1.5 col-span-8 sm:col-span-8 md:col-span-8 lg:col-span-5 xl:col-span-4 2xl:col-span-3">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const data = Object.fromEntries(formData);
              handleSubmit({ emailAddress: data.emailAddress as string });
            }}
          >
            <Input
              isRequired
              errorMessage="Email address is required"
              label="Email Address"
              labelPlacement="outside"
              name="emailAddress"
              placeholder="Enter your email address"
              type="email"
              classNames={{
                inputWrapper: "rounded-none border-1",
                label: "label",
              }}
              onChange={handleInputChange}
              disabled={loading}
            />

            <div className="flex w-full gap-4 items-center justify-end">
              <a
                className="navigate-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/account/login");
                }}
              >
                Cancel
              </a>

              <Button
                isDisabled={loading}
                size="md"
                className="rounded-none"
                color="primary"
                type="submit"
              >
                {loading && messageType !== "success" ? "Sending..." : loading && messageType === "success" ? "Redirecting..." : "Send"}
              </Button>
            </div>

            <h6 className="text-sm">
              Please enter your email address and click ‘Send’. Your password
              will be reset and a new one sent to your registered email address.
            </h6>

            {message && messageType && (
              <FormAlert message={message} messageType={messageType} />
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
